import { useTheme } from "@emotion/react";
import {
  faAngleDown,
  faAngleUp,
  faMinus,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Close, Search, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery,
} from "@mui/material";
import { renderTimeViewClock } from "@mui/x-date-pickers";
import { Info, Question, WarningCircle } from "phosphor-react";
import { forwardRef, Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import useTamanhoTela from "../../utils/useTamanhoTela";
import { IconeBotao } from "../BottomSheetAtalhos/styles";
import { CheckBox } from "../Checkbox";
import Chip from "../Chip";
import {
  AutocompleteCustomizado,
  CampoCorBase,
  CampoDeDataBase,
  CampoDeDataSemHoraBase,
  CampoDeHoraBase,
  CampoDeInputNumericoAlternativoBase,
  CampoDeTextoBase,
  CampoDeTextoBaseAutoComplete,
  CampoDeTextoBaseSelecionarCliente,
  CampoDeTextoCustom,
  ContainerData,
  CustomTooltip,
  FormularioCustomizado,
  FormularioCustomizadoMultiSelect,
  FormularioCustomizadoSelecao,
  InputCustomizado,
  InputPorcentagemWayservice,
  NumberFormatBaseInput,
  NumericFormatCustomizado,
  NumericFormatCustomizadoVertical,
  NumericFormatInput,
  TextFieldMonetario,
} from "./styles";

export const CampoDeTexto = forwardRef(({
  className = "",
  disabled = false,
  error = false,
  focusProximo,
  fundoTransparente = false,
  inputProps: inputPropsProp,
  maxlength = 999,
  mensagemPopup = "",
  mostrarSetasInputNumerico = false,
  ocultarAdornmentErro = false,
  ocultarSetas = false,
  onBlur,
  onClickSearch = () => {},
  onKeyDown = () => {},
  popup = false,
  refs,
  register,
  search = false,
  style,
  tabindex,
  titulo,
  type = "text",
  typeNumber = false,
  value,
  variant = "standard",
  ...rest
}, ref) => {
  const { t } = useTranslation("common");

  // Filtrar propriedades que não devem ser passadas para o DOM
  const { typeNumber: _, ocultarSetas: __, $fundoTransparente: ___, ...filteredRest } = rest;
  const showWarningAdornment = error && !ocultarAdornmentErro;
  const isMobile = useMediaQuery("(max-width: 768px)");

  const inputPropsMerged = {
    tabIndex: tabindex,
    maxLength: maxlength,
    ...(isMobile && typeNumber && { inputMode: "numeric", pattern: "[0-9]*" }),
    ...inputPropsProp,
  };

  return popup ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <CampoDeTextoBase
        className={className}
        typeNumber={typeNumber}
        $mostrarSetasInputNumerico={mostrarSetasInputNumerico}
        label={titulo}
        error={error}
        $fundoTransparente={fundoTransparente}
        variant={variant}
        style={{
          ...style,
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? "not-allowed" : "default",
        }}
        value={value}
        onBlur={focusProximo || onBlur}
        inputProps={inputPropsMerged}
        InputProps={{
          endAdornment: showWarningAdornment && <WarningCircle size={22} color="#f44336" />,
        }}
        tabIndex={tabindex}
        ref={refs}
        inputRef={ref}
        ocultarSetas={ocultarSetas}
        onKeyDown={onKeyDown}
        type={type}
        disabled={disabled}
        placeholder={t("placeholders.typeHere")}
        {...register}
        {...filteredRest}
      />

      <CustomTooltip title={mensagemPopup} placement="bottom" arrow>
        <Question color="rgba(0, 100, 149, 1)" size={24} weight="bold" />
      </CustomTooltip>
    </div>
  ) : (
    <CampoDeTextoBase
      className={className}
      typeNumber={typeNumber}
      $mostrarSetasInputNumerico={mostrarSetasInputNumerico}
      label={titulo}
      error={error}
      $fundoTransparente={fundoTransparente}
      variant={variant}
      style={{
        ...style,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
      value={value}
      onBlur={focusProximo || onBlur}
      inputProps={inputPropsMerged}
      InputLabelProps={{
        shrink: value !== undefined && value !== null && value !== "" ? true : undefined,
      }}
      InputProps={{
        endAdornment: error && showWarningAdornment && <WarningCircle size={22} color="#f44336" />,
        endAdornment: search && <Search size={23} onClick={() => onClickSearch(value)} />,
      }}
      tabIndex={tabindex}
      ref={refs}
      inputRef={ref}
      ocultarSetas={ocultarSetas}
      onKeyDown={onKeyDown}
      type={type}
      disabled={disabled}
      placeholder={t("placeholders.typeHere")}
      {...register}
      {...filteredRest}
    />
  );
});

CampoDeTexto.displayName = "CampoDeTexto";

export const CampoDeTextoSelecionarCliente = forwardRef(({
  titulo,
  fundoTransparente = false,
  style,
  value,
  error = false,
  disabled = false,
  ...rest
}, ref) => {
  return (
    <CampoDeTextoBaseSelecionarCliente
      {...rest}
      ref={ref}
      label={titulo}
      variant="standard"
      style={style}
      value={value == undefined ? "" : value}
      error={error}
      className={disabled && "not-allowed"}
      disabled={disabled}
    />
  );
});

CampoDeTextoSelecionarCliente.displayName = "CampoDeTextoSelecionarCliente";

export const CampoDeSenha = ({ titulo, variant = "standard", error = false, ...rest }) => {
  const [mostrarSenha, setMostrarSenha] = useState(false);

  function controlarMostrarSenha() {
    setMostrarSenha(!mostrarSenha);
  }

  return (
    <FormularioCustomizado variant="standard" value={rest.value}>
      <InputCustomizado htmlFor="standard-adornment-password" error={error}>
        {titulo}
      </InputCustomizado>
      <Input
        {...rest}
        variant={"filled"}
        id="standard-adornment-password"
        type={mostrarSenha ? "text" : "password"}
        error={error}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={controlarMostrarSenha}
              onMouseDown={controlarMostrarSenha}
              style={{ position: "relative", bottom: "0px" }}
            >
              {mostrarSenha ? (
                <VisibilityOff style={{ color: error ? "#f44336" : "inherit" }} />
              ) : (
                <Visibility style={{ color: error ? "#f44336" : "inherit" }} />
              )}
            </IconButton>
          </InputAdornment>
        }
      />
    </FormularioCustomizado>
  );
};

export const CampoDeData = ({
  titulo,
  style,
  error,
  errorMessage,
  refs,
  limparCampo,
  mostrarHora = true,
  somenteMesAno = false,
  onBlurTextField = () => {},
  ocultarTabsMobile = false,
  tituloToolbar = null,
  ...rest
}) => {
  return (
    <CampoDeDataBase
      {...rest}
      ref={refs}
      label={titulo}
      variant="standart"
      format={mostrarHora ? "DD/MM/YYYY - HH:mm" : "DD/MM/YYYY"}
      dayOfWeekFormatter={(day) => day.charAt(0).toUpperCase()}
      views={mostrarHora ? null : ["year", "month", "day"]}
      localeText={{
        fieldYearPlaceholder: () => "YYYY",
        fieldMonthPlaceholder: () => "MM",
        fieldDayPlaceholder: () => "DD",
        fieldHoursPlaceholder: () => "HH",
        fieldMinutesPlaceholder: () => "MM",
      }}
      slotProps={{
        field: {
          clearable: true,
          onClear: limparCampo,
        },
        textField: {
          variant: "standard",
          error: error,
          helperText: errorMessage,
          onBlur: onBlurTextField,
          inputProps: {
            placeholder: mostrarHora ? "DD/MM/YYYY - HH:MM" : "DD/MM/YYYY",
          },
          sx: {
            "& .MuiInputBase-input": {
              textTransform: "uppercase !important",
            },
            ...style,
          },
        },
        toolbar: tituloToolbar ? { toolbarTitle: tituloToolbar } : {},
        tabs: ocultarTabsMobile
          ? {
              hidden: true,
            }
          : null,
        ...(limparCampo
          ? {
              field: {
                clearable: true,
                onClear: limparCampo,
              },
            }
          : {}),
      }}
    />
  );
};

export const CampoDeSelecao = ({
  MenuProps,
  ajustarMenuAoCampo = false,
  campoVazio = true,
  carregando = false,
  defaultValue,
  disabled,
  error = false,
  iconeInfo = false,
  iconeInfoId = null,
  iconeQuestion = false,
  iconeQuestionId = null,
  iconeSearch = false,
  items = [],
  limparCampo,
  limitarMenuAoCampo = false,
  maxOpcaoWidth,
  multiple = false,
  onChange,
  onOpen,
  referenciaNome,
  referenciaValor,
  titulo,
  valor,
  valorPadrao,
  ...rest
}) => {
  const theme = useTheme();
  const { largura } = useTamanhoTela();
  const isMobileSelect = largura < 640;
  const [menuAbertoMobile, setMenuAbertoMobile] = useState(false);
  const [larguraCampoSelecao, setLarguraCampoSelecao] = useState(0);
  const [larguraMenuLimitado, setLarguraMenuLimitado] = useState(null);
  const campoSelecaoRef = useRef(null);
  const touchSelecaoRef = useRef({
    startX: 0,
    startY: 0,
    moved: false,
  });

  const ellipsisStyle = maxOpcaoWidth
    ? {
        maxWidth: maxOpcaoWidth,
        minWidth: 0,
        width: maxOpcaoWidth === "100%" ? "100%" : undefined,
        boxSizing: "border-box",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
      }
    : null;

  useEffect(() => {
    if (!limitarMenuAoCampo) {
      return;
    }

    const larguraAtual = campoSelecaoRef.current?.getBoundingClientRect().width;

    if (larguraAtual) {
      setLarguraMenuLimitado(larguraAtual);
    }
  }, [largura, limitarMenuAoCampo]);

  const normalizarTexto = (texto = "") =>
    texto
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  useEffect(() => {
    if (!ajustarMenuAoCampo || !campoSelecaoRef.current) {
      return undefined;
    }

    const campoSelecao = campoSelecaoRef.current;
    const atualizarLarguraCampo = () => {
      const novaLargura = campoSelecao.getBoundingClientRect().width;

      if (novaLargura > 0) {
        setLarguraCampoSelecao(novaLargura);
      }
    };

    atualizarLarguraCampo();
    window.addEventListener("resize", atualizarLarguraCampo);

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(atualizarLarguraCampo) : null;

    resizeObserver?.observe(campoSelecao);

    return () => {
      window.removeEventListener("resize", atualizarLarguraCampo);
      resizeObserver?.disconnect();
    };
  }, [ajustarMenuAoCampo]);

  const obterTextoValorSelecionado = () => {
    const valoresSelecionados = multiple && Array.isArray(valor) ? valor : [valor];

    return valoresSelecionados
      .map((valorSelecionado) => {
        const itemSelecionado = items.find((item) => {
          const valorItem = typeof item === "object" ? item?.[referenciaValor || "valor"] : item;

          return String(valorItem) === String(valorSelecionado);
        });

        if (typeof itemSelecionado === "object") {
          return itemSelecionado?.[referenciaNome || "titulo"];
        }

        return itemSelecionado;
      })
      .filter(Boolean)
      .join(", ");
  };

  const larguraMenu =
    ajustarMenuAoCampo && larguraCampoSelecao > 0
      ? `${larguraCampoSelecao}px !important`
      : undefined;

  const scrollbarMenuSx = {
    "&::-webkit-scrollbar": {
      height: "8px",
      width: "8px",
      display: "block",
      backgroundColor: theme.cores.brand20,
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.cores.actionActive,
      height: "8px",
      borderRadius: "5px",
      border: `1px solid ${theme.cores.surfaceBackground1}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.adaptativo("brand40Default", "brand80"),
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
      borderRadius: "10px",
    },
  };

  const resolvedMenuProps = {
    ...(MenuProps || {}),
    ...(MenuProps?.disableScrollLock === undefined ? { disableScrollLock: true } : {}),
    ...(MenuProps?.keepMounted === undefined ? { keepMounted: true } : {}),

    PaperProps: {
      ...(MenuProps?.PaperProps || {}),
      sx: {
        maxHeight: isMobileSelect ? 240 : 280,
        overflowY: "auto",
        ...scrollbarMenuSx,
        ...(MenuProps?.PaperProps?.sx || {}),
        ...(limitarMenuAoCampo && larguraMenuLimitado
          ? {
              width: `${larguraMenuLimitado}px`,
              minWidth: `${larguraMenuLimitado}px !important`,
              maxWidth: `${larguraMenuLimitado}px`,
              overflowX: "hidden",
            }
          : {}),
        ...(isMobileSelect
          ? {
              width: "90vw",
              maxWidth: "90vw",
              minWidth: "90vw !important",
            }
          : {}),
        ...(larguraMenu
          ? {
              boxSizing: "border-box",
              maxWidth: larguraMenu,
              minWidth: larguraMenu,
              overflowX: "hidden",
              width: larguraMenu,
            }
          : {}),
        zIndex: 2000,
      },
    },

    MenuListProps: {
      ...(MenuProps?.MenuListProps || {}),
      sx: {
        overflowY: "visible",
        ...(MenuProps?.MenuListProps?.sx || {}),
        ...(isMobileSelect
          ? {
              maxWidth: "90vw",
              width: "100%",
            }
          : {}),
        ...(larguraMenu
          ? {
              boxSizing: "border-box",
              maxWidth: "100%",
              width: "100%",
            }
          : {}),
      },
    },
  };

  const criarEventosTouchSelecao = (value) =>
    isMobileSelect
      ? {
          onTouchStart: (e) => {
            const touch = e.touches?.[0];
            if (!touch) return;

            touchSelecaoRef.current = {
              startX: touch.clientX,
              startY: touch.clientY,
              moved: false,
            };
          },
          onTouchMove: (e) => {
            const touch = e.touches?.[0];
            if (!touch) return;

            const deltaX = Math.abs(touch.clientX - touchSelecaoRef.current.startX);
            const deltaY = Math.abs(touch.clientY - touchSelecaoRef.current.startY);

            if (deltaX > 8 || deltaY > 8) {
              touchSelecaoRef.current.moved = true;
            }
          },
          onTouchEnd: (e) => {
            e.stopPropagation();

            if (touchSelecaoRef.current.moved) {
              return;
            }

            e.preventDefault();
            onChange?.({ target: { value } });
            setMenuAbertoMobile(false);
          },
        }
      : {};

  function GerenciarObjetosOuTextos() {
    return items
      .map((item, index) => {
        if (!item) {
          return null;
        }

        if (typeof item === "string") {
          const estiloItemMobile = isMobileSelect
            ? {
                maxWidth: "90vw",
              }
            : undefined;

          const estiloConteudo = isMobileSelect
            ? {
                maxWidth: "90vw",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                lineHeight: 1.3,
                display: "block",
              }
            : ellipsisStyle;

          return (
            <MenuItem
              style={estiloItemMobile}
              key={titulo + item + index}
              value={item}
              disabled={item?.disabled}
              {...criarEventosTouchSelecao(item)}
            >
              {estiloConteudo ? <span style={estiloConteudo}>{item}</span> : item}
            </MenuItem>
          );
        } else if (typeof item === "object") {
          const valorItem = item[referenciaValor ? referenciaValor : "valor"];
          const nomeItem = item[referenciaNome ? referenciaNome : "titulo"];

          if (valorItem === undefined || valorItem === null) {
            return null;
          }

          let finalStyle = {};
          if (item["esconderMenuItem"]) {
            finalStyle = { display: "none" };
          } else if (isMobileSelect) {
            finalStyle = {
              maxWidth: "90vw",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              lineHeight: 1.3,
              display: "block",
            };
          } else if (ellipsisStyle) {
            finalStyle = ellipsisStyle;
          }

          const numeroItem = item.numero;
          const temNumero = numeroItem !== undefined && numeroItem !== null;

          return (
            <MenuItem
              style={finalStyle}
              key={titulo + valorItem + index}
              value={valorItem}
              disabled={item?.disabled || item?.isLoadingItem}
              {...criarEventosTouchSelecao(valorItem)}
            >
              {temNumero ? (
                <span style={{ display: "flex", width: "100%", gap: 16 }}>
                  <span style={{ ...finalStyle, flex: 1 }}>{nomeItem || valorItem}</span>
                  <span>{numeroItem}</span>
                </span>
              ) : ellipsisStyle ? (
                <span style={finalStyle}>{nomeItem || valorItem}</span>
              ) : (
                nomeItem || valorItem
              )}
            </MenuItem>
          );
        }

        return null;
      })
      .filter(Boolean);
  }

  return (
    <FormularioCustomizadoSelecao
      ref={campoSelecaoRef}
      variant="standard"
      {...rest}
      value={valor}
      disabled={disabled}
      error={error}
    >
      <InputLabel
        shrink
        style={{
          display: iconeSearch ? "flex" : "",
          alignItems: iconeSearch ? "center" : "",
          gap: iconeSearch ? 10 : "",
          paddingLeft: iconeSearch ? 16 : "",
        }}
        defaultValue={"Atacado"}
        error={error}
      >
        {iconeSearch && (
          <FontAwesomeIcon
            icon={faSearch}
            style={{
              color: theme.adaptativo("neutral50", "neutral100"),
              width: 15,
              height: 15,
              weight: "bold",
            }}
          />
        )}
        {titulo}
      </InputLabel>
      <Select
        onOpen={(event) => {
          if (isMobileSelect) setMenuAbertoMobile(true);
          if (typeof onOpen === "function") onOpen(event);
        }}
        onClose={() => {
          if (isMobileSelect) setMenuAbertoMobile(false);
        }}
        open={isMobileSelect ? menuAbertoMobile : undefined}
        value={items.length > 0 && valor ? valor : multiple ? [] : campoVazio ? "Selecione" : ""}
        multiple={multiple}
        label={titulo}
        onChange={onChange}
        defaultValue={defaultValue}
        error={error}
        className={iconeInfo ? "selectInputComInfo" : ""}
        MenuProps={resolvedMenuProps}
        native={false}
        displayEmpty
        renderValue={(selected) => {
          if (multiple) {
            if (!selected || selected.length === 0) {
              return (
                <span
                  style={{
                    color: theme.adaptativo("neutral50", "neutral70"),
                    opacity: 0.7,
                  }}
                >
                  Selecione
                </span>
              );
            }
            return Array.isArray(selected) ? selected.join(", ") : selected;
          }

          if (!selected || selected === "" || selected === "Selecione") {
            return (
              <span
                style={{
                  color: theme.adaptativo("neutral50", "neutral70"),
                  opacity: 0.7,
                }}
              >
                Selecione
              </span>
            );
          }

          if (typeof items[0] === "object") {
            const itemEncontrado = items.find((i) => i[referenciaValor || "valor"] === selected);
            return itemEncontrado ? itemEncontrado[referenciaNome || "titulo"] : selected;
          }

          return selected;
        }}
        SelectDisplayProps={
          ajustarMenuAoCampo ? { title: obterTextoValorSelecionado() } : undefined
        }
        sx={{
          ...(limparCampo ? { paddingRight: "28px" } : {}),
          ...(ajustarMenuAoCampo
            ? {
                "& .MuiSelect-select": {
                  boxSizing: "border-box",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }
            : {}),
        }}
      >
        {campoVazio && <MenuItem value={"Selecione"}>Selecione</MenuItem>}
        {GerenciarObjetosOuTextos()}
      </Select>
      {limparCampo && !disabled && (
        <IconButton
          size="small"
          aria-label={`Limpar ${titulo}`}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            limparCampo(event);
          }}
          style={{
            position: "absolute",
            right: 40,
            bottom: 8,
            zIndex: 2,
            padding: 4,
          }}
        >
          <Close fontSize="small" style={{ marginRight: 0 }} />
        </IconButton>
      )}
      {iconeInfo && (
        <Question
          size={24}
          weight="bold"
          id={iconeInfoId}
          style={{
            fill: theme.cores.brand40Default,
            color: theme.cores.brand40Default,
            position: "absolute",
            right: 0,
            bottom: 15,
            cursor: "pointer",
          }}
        />
      )}

      {iconeQuestion && (
        <Question
          id={iconeQuestionId}
          color="rgba(0, 100, 149, 1)"
          size={21}
          style={{
            position: "absolute",
            right: 0,
            bottom: 15,
            cursor: "default",
          }}
        />
      )}

      {carregando && (
        <CircularProgress
          size={16}
          color="inherit"
          style={{
            position: "absolute",
            right: 8,
            bottom: 18,
          }}
        />
      )}
    </FormularioCustomizadoSelecao>
  );
};

export default function CampoDeSelecaoChips({
  titulo,
  itemsSelecionados = [],
  setItemsSelecionados,
  style,
  items = [],
  error = false,
  disabled,
  focusBtnSalvar,
  layoutFix,
  chipsMultiline = true,
  /** Altura mínima da área dos chips dentro do select (ex.: 48 para campo maior) */
  alturaMinimaAreaChips = 27,
  /** Exibe botão "X" para limpar todas as seleções */
  mostrarBotaoLimparSelecao = false,
  /** Espaço (px) entre o checkbox e o texto de cada opção no menu */
  gapCheckboxMenuItem = 10,
}) {
  const mudarValores = (event) => {
    const {
      target: { value },
    } = event;
    if (value.includes("Selecionar todos")) {
      setItemsSelecionados(items);

      //Fechar opções do select ao selecionar todas
      document.querySelector(".MuiBackdrop-invisible").click();

      return;
    }

    //Ele pega o Valor do evento
    //Se o valor for uma string, ele separa por virgula e o transforma em um array
    //Se não, ele pega o valor e retorna o valor unico
    setItemsSelecionados(typeof value === "string" ? value.split(",") : value);
  };

  const deletarItem = (valor) => {
    setItemsSelecionados([...itemsSelecionados.filter((item) => item !== valor)]);
  };

  const selecionarTodos = (e) => {
    e.stopPropagation();

    setItemsSelecionados(...items);
  };

  const onKeyUpCapture = (e) => {
    if (e.key === "Tab") {
      if (typeof focusBtnSalvar === "function") {
        focusBtnSalvar();
      }
      e.preventDefault();
      return false;
    }
  };

  const [chipsNaoExibidos, setChipsNaoExibidos] = useState([]);
  const boxChipsRef = useRef(null);

  const alturaChipsPx =
    typeof alturaMinimaAreaChips === "number"
      ? alturaMinimaAreaChips
      : parseInt(String(alturaMinimaAreaChips), 10) || 27;

  // Função para verificar quais chips não estão totalmente visíveis
  const verificarChipsVisiveis = useCallback(() => {
    if (!boxChipsRef.current || chipsMultiline) {
      setChipsNaoExibidos([]);
      return;
    }

    const boxContainer = boxChipsRef.current;
    const containerRect = boxContainer.getBoundingClientRect();
    const containerRight = containerRect.right;

    const chips = boxContainer.querySelectorAll(".MuiChip-root");
    const chipsOcultos = [];

    chips.forEach((chip) => {
      const chipRect = chip.getBoundingClientRect();
      const chipLabel = chip.querySelector(".MuiChip-label");
      const labelText = chipLabel ? chipLabel.textContent : "";

      // Verifica se o chip está parcialmente ou totalmente fora da área visível
      if (chipRect.right > containerRight + 10 || chipRect.left >= containerRight) {
        chipsOcultos.push(labelText);
      }
    });

    setChipsNaoExibidos(chipsOcultos);
  }, [chipsMultiline]);

  // Observar mudanças nos chips selecionados
  useEffect(() => {
    verificarChipsVisiveis();
  }, [itemsSelecionados, verificarChipsVisiveis]);

  // Observer para mudanças de tamanho do container
  useEffect(() => {
    if (!boxChipsRef.current || chipsMultiline) return;

    const resizeObserver = new ResizeObserver(() => {
      verificarChipsVisiveis();
    });

    resizeObserver.observe(boxChipsRef.current);

    window.addEventListener("resize", verificarChipsVisiveis);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", verificarChipsVisiveis);
    };
  }, [verificarChipsVisiveis, chipsMultiline]);

  return (
    <FormularioCustomizadoMultiSelect
      variant="standard"
      style={style}
      disabled={disabled}
      layoutFix={layoutFix}
      chipsMultiline={chipsMultiline}
    >
      <InputLabel error={error}>{titulo}</InputLabel>
      <Select
        error={error}
        value={itemsSelecionados ? itemsSelecionados : []}
        label={titulo}
        onChange={mudarValores}
        onKeyUpCapture={onKeyUpCapture}
        multiple
        // input={<OutlinedInput label="Chip" />}
        renderValue={(selected) => (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              paddingBottom: 4,
              width: "100%",
            }}
          >
            <Box
              ref={boxChipsRef}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                minHeight: `${alturaChipsPx}px`,
                flex: 1,
                minWidth: 0,
              }}
              className="boxChips"
            >
              {selected.map((value) => (
                <Chip
                  label={value}
                  color="primary"
                  variant="outlined"
                  style={{
                    pointerEvents: disabled ? "none" : "all",
                  }}
                  onDelete={() => deletarItem(value)}
                  onMouseDown={(event) => event.stopPropagation()}
                  key={value + "chipSelect"}
                />
              ))}
            </Box>
            {mostrarBotaoLimparSelecao && selected && selected.length > 0 && (
              <IconButton
                aria-label="Limpar seleção"
                disabled={disabled}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setItemsSelecionados([]);
                }}
                sx={{
                  flexShrink: 0,
                  alignSelf: "center",
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  minHeight: 40,
                  padding: 0,
                  borderRadius: "50%",
                }}
              >
                <Close sx={{ fontSize: 22 }} />
              </IconButton>
            )}
            {chipsNaoExibidos.length > 0 && !chipsMultiline && (
              <CustomTooltip
                title={chipsNaoExibidos.join(", ")}
                placement="bottom"
                arrow
                style={{
                  color: "black",
                }}
              >
                ...
              </CustomTooltip>
            )}
          </div>
        )}
        // MenuProps={MenuProps}
      >
        <MenuItem value="Selecionar todos">Selecionar todos</MenuItem>
        {items.map((item) => (
          <MenuItem key={item} value={item}>
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: `${gapCheckboxMenuItem}px`,
                width: "100%",
              }}
            >
              <CheckBox value={itemsSelecionados.includes(item)} />
              <span>{item}</span>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormularioCustomizadoMultiSelect>
  );
}

export function CampoDeInputMonetario({
  style = {},
  onChange = () => {},
  value,
  label,
  titulo,
  error,
  onBlur = () => {},
  onFocus = () => {},
  onClick = () => {},
  valorMax = 9999999.99,
  tabindex,
  pedidoCompra = false,
  refs,
  classess = "",
  disabled = false,
  customId = "",
  validarLimites = false,
}) {
  const { t } = useTranslation("common");
  const [erroValidacao, setErroValidacao] = useState("");
  const [valorInicialSetado, setValorInicialSetado] = useState(false);
  // Aviso
  // Diferente dos outros campos de Input, por fazer o tratamento, este retorna diretamente o Valor, e não o
  // e.target.value como os outros.

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
    placeholder: "R$ 0,00",
    InputProps: {
      InputAdornment: <InputAdornment position="start">R$</InputAdornment>,
      placeholder: "R$ 0,00",
    },
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState("");
  const inputFocadoRef = useRef(false);

  useEffect(() => {
    if (value === "" || value == null || value === undefined) {
      setRepresentacaoVisual("");
      return;
    }

    if (inputFocadoRef.current) return;

    setRepresentacaoVisual(
      typeof value === "number"
        ? tratarLimiteValor(value).toLocaleString("pt-br", {
            style: "currency",
            currency: "BRL",
          })
        : value,
    );
  }, [value]);

  useEffect(() => {
    if (!valorInicialSetado) {
      if (value != undefined) {
        setValorInicialSetado(true);
        let valor = value;
        valor = tratarLimiteValor(valor);
        setRepresentacaoVisual(
          valor.toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
        );
      }
    }
  });

  function onChangeInterno(valorAtualizado) {
    let valor = parseFloat(
      valorAtualizado.target.value.replace("R$", "").replaceAll(".", "").replaceAll(",", "."),
    );

    valor = tratarLimiteValor(valor);

    onChange(validarLimites && !Number.isFinite(valor) ? 0 : valor);

    setRepresentacaoVisual(valorAtualizado.target.value);
  }

  function tratarLimiteValor(valor) {
    let novoValor = valor;
    novoValor = tratarValorMax(novoValor);
    return novoValor;
  }

  function tratarValorMax(valor) {
    if (validarLimites) return valor;

    if (valorMax || pedidoCompra) {
      const valorMaxAtualizado = pedidoCompra ? 9999.99 : valorMax;
      const max = valorMaxAtualizado * 100;
      if (valor > max) {
        return max;
      }
    }

    return valor;
  }

  const formatCurrencyByEnd = (value) => {
    if (!Number(value)) return "";

    if (validarLimites && (!Number.isFinite(Number(value)) || Number(value) > valorMax * 100)) {
      return value;
    }

    const valor = tratarLimiteValor(value);

    const amount = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(parseFloat(valor) / 100);

    return `${amount}`;
  };

  function verificarLimites({ value, floatValue }) {
    const limite = pedidoCompra ? 9999.99 : valorMax;
    if (value !== "" && (!Number.isFinite(floatValue) || (limite && floatValue / 100 > limite))) {
      setErroValidacao(
        t("errors.maximumMonetaryValue", {
          value: limite.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        }),
      );
      return false;
    }
    if (floatValue < 0) {
      setErroValidacao(t("errors.nonNegativeMonetaryValue"));
      return false;
    }
    setErroValidacao("");
    return true;
  }

  return (
    <NumberFormatBaseInput
      className={`input-CampoDeInputMonetario ${classess}`}
      id={customId}
      format={formatCurrencyByEnd}
      style={{
        ...style,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
      label={label || titulo}
      onChange={onChangeInterno}
      isAllowed={validarLimites ? verificarLimites : undefined}
      onKeyDown={(e) => {
        if (validarLimites && /[-−]/.test(e.key) && e.key.length === 1) {
          e.preventDefault();
          setErroValidacao(t("errors.nonNegativeMonetaryValue"));
        }
      }}
      onPaste={(e) => {
        if (validarLimites && /[-−]/.test(e.clipboardData.getData("text"))) {
          e.preventDefault();
          setErroValidacao(t("errors.nonNegativeMonetaryValue"));
        }
      }}
      onBlur={(e) => {
        inputFocadoRef.current = false;
        onBlur(e);
      }}
      onClick={onClick}
      inputRef={refs}
      value={representacaoVisual}
      thousandSeparator="."
      decimalSeparator=","
      prefix="R$ "
      decimalScale={2}
      fixedDecimalScale
      error={error || !!erroValidacao}
      helperText={erroValidacao}
      customInput={TextFieldMonetario}
      onFocus={(e) => {
        inputFocadoRef.current = true;
        onFocus(e);
      }}
      disabled={disabled}
      {...propriedadesMaterialUIStandard}
      InputProps={{ tabIndex: tabindex }}
      InputLabelProps={{
        shrink: !!representacaoVisual ? true : undefined,
      }}
    />
  );
}

export function CampoDeInputMonetarioPermiteZero({
  style = {},
  onChange = () => {},
  value,
  label,
  titulo,
  error,
  onBlur = () => {},
  disabled,
  permitirZero = false,
  valorMax = 9999999999.99,
  classess = "",
  tabindex,
  refs,
  refInputProps,
}) {
  const [valorInicialSetado, setValorInicialSetado] = useState(false);
  // Aviso
  // Diferente dos outros campos de Input, por fazer o tratamento, este retorna diretamente o Valor, e não o
  // e.target.value como os outros.

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
    placeholder: "R$ 0,00",
    InputProps: {
      InputAdornment: <InputAdornment position="start">R$</InputAdornment>,
      placeholder: "R$ 0,00",
    },
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState("");
  useEffect(() => {
    if (value == "" || String(value) == "NaN" || value == "0") {
      setRepresentacaoVisual("0");
    } else if (value) {
      setRepresentacaoVisual(
        permitirZero
          ? value?.toLocaleString("pt-br", {
              style: "currency",
              currency: "BRL",
            })
          : "",
      );
    }
  }, [value]);
  useEffect(() => {
    if (!valorInicialSetado) {
      if (value != undefined) {
        setValorInicialSetado(true);
        let valor = value;
        valor = tratarLimiteValor(valor);
        setRepresentacaoVisual(
          valor.toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
        );
      }
    }
  });

  function onChangeInterno(valorAtualizado) {
    let valor = parseFloat(
      valorAtualizado.target.value.replace("R$", "").replaceAll(".", "").replaceAll(",", "."),
    );

    valor = tratarLimiteValor(valor);

    onChange(valor);
    setRepresentacaoVisual(valorAtualizado.target.value);
  }

  function tratarLimiteValor(valor) {
    let novoValor = valor;
    novoValor = tratarValorMax(novoValor);
    return novoValor;
  }

  function tratarValorMax(valor) {
    if (valorMax) {
      const max = valorMax * 100;
      if (valor > max) {
        return max;
      }
    }

    return valor;
  }

  const formatCurrencyByEnd = (value) => {
    if (!Number(value) && !permitirZero) return "";

    const valor = tratarLimiteValor(value);

    const amount = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(parseFloat(valor) / 100);

    return value ? `${amount}` : "R$ 0,00";
  };

  return (
    <NumberFormatBaseInput
      className={`input-CampoDeInputMonetario ${classess}`}
      // {...rest}
      format={formatCurrencyByEnd}
      style={{
        ...style,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
      label={label || titulo}
      //onValueChange={onChangeInterno}
      onChange={onChangeInterno}
      onBlur={onBlur}
      value={representacaoVisual}
      thousandSeparator="."
      decimalSeparator=","
      prefix="R$ "
      decimalScale={2}
      fixedDecimalScale
      error={error}
      customInput={TextFieldMonetario}
      disabled={disabled}
      {...propriedadesMaterialUIStandard}
      ref={refs}
      InputProps={{ tabIndex: tabindex, ref: refInputProps }}
      InputLabelProps={{ shrink: !!representacaoVisual ? true : undefined }}
    />
  );
}

export function CampoDeInputNumerico({
  style = {},
  onChange,
  value,
  label,
  error,
  onBlur = () => {},
  onFocus = () => {},
  disabled,
  tabindex,
  permitirZero = false,
  placeholder,
  permitirZeroAEsquerda = false,
  defaultValue,
  allowNegative = false,
  minValue,
  maxValue,
  classess = "",
  maxLength,
  endAdornment,
  inverterSeparadores = false,
  inputProps: inputPropsProp,
  ...rest
}) {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
    InputProps: {
      placeholder: placeholder,
      endAdornment: endAdornment,
    },
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState(
    defaultValue !== undefined ? defaultValue : "",
  );

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setRepresentacaoVisual(value);
    } else {
      setRepresentacaoVisual(permitirZero ? value : "");
    }
  }, [value, permitirZero]);

  const checkIsAllowed = (values) => {
    const { floatValue, formattedValue, value: rawValue } = values;

    if (rawValue === "" || rawValue === "-") return true;

    if (maxLength !== undefined && formattedValue.length > maxLength) {
      return false;
    }

    if (maxValue !== undefined && floatValue !== undefined) {
      const valorAtualNum = parseFloat(
        String(representacaoVisual).replaceAll(".", "").replaceAll(",", "."),
      );
      if (!isNaN(valorAtualNum) && valorAtualNum >= maxValue && floatValue > maxValue) {
        return false;
      }
    }

    return true;
  };

  function onChangeInterno(valorAtualizado) {
    let valor = valorAtualizado.target.value;

    if (maxLength && valor.length > maxLength) {
      valor = valor.slice(0, maxLength);
    }

    let texto = valor.replace("+", "");

    if (!allowNegative) {
      texto = texto.replaceAll("-", "");
    }

    let numVal = parseFloat(
      inverterSeparadores
        ? texto.replaceAll(".", "").replaceAll(",", ".")
        : texto.replaceAll(",", "").replaceAll(".", ""),
    );

    if (!isNaN(numVal) && maxValue !== undefined && numVal > maxValue) {
      numVal = maxValue;
      valor = String(maxValue);
    }

    if (permitirZeroAEsquerda) {
      onChange(texto.replaceAll(",", "").replaceAll(".", ""));
    } else {
      onChange(isNaN(numVal) ? "" : numVal);
    }

    setRepresentacaoVisual(valor);
  }

  function handleBlur(e) {
    const numVal = parseFloat(String(representacaoVisual).replaceAll(".", "").replaceAll(",", "."));

    if (minValue !== undefined && (isNaN(numVal) || numVal < minValue)) {
      setRepresentacaoVisual(minValue);
      onChange(minValue);
    }

    onBlur(e);
  }

  return (
    <NumericFormatInput
      className={classess}
      style={{ ...style }}
      label={label}
      onChange={onChangeInterno}
      onBlur={handleBlur}
      onFocus={onFocus}
      value={representacaoVisual}
      allowNegative={allowNegative}
      allowLeadingZeros={permitirZeroAEsquerda}
      error={error}
      customInput={TextFieldMonetario}
      disabled={disabled}
      isAllowed={checkIsAllowed}
      inputProps={{
        tabIndex: tabindex,
        maxLength: maxLength,
        ...(isMobile && { inputMode: "numeric", pattern: "[0-9]*" }),
        ...inputPropsProp,
      }}
      decimalSeparator={inverterSeparadores ? "," : "."}
      thousandSeparator={inverterSeparadores ? "." : undefined}
      decimalScale={inverterSeparadores ? 2 : undefined}
      fixedDecimalScale={inverterSeparadores ? true : undefined}
      {...rest}
      {...propriedadesMaterialUIStandard}
    />
  );
}

export function CampoAutoComplete({
  titulo,
  style,
  opcoes,
  opcaoUnica = false,
  disableClearable = false,
  error = false,
  onChange,
  value,
  referenciaKey,
  finalizadora = false,
  referenciaNome,
  opcaoTodos = false,
  tabindex,
  refs,
  focusProximo,
  isOptionEqualToValue,
  disabled,
  buscarOpcoes,
  iconeSearch = false,
  iconeInfo = false,
  iconeInfoId = null,
  iconeQuestion,
  iconeQuestionId,
  disablePortal = true,
  sxMaxHeight100 = 250,
}) {
  const { t } = useTranslation("common");
  const [texto, setTexto] = useState("");
  const theme = useTheme();

  if (opcaoTodos) {
    const opcaoJaExiste = (obj) => obj.id === "todos";

    if (opcoes == undefined) {
      opcoes = [];
    }

    if (!opcoes.some(opcaoJaExiste)) {
      opcoes.unshift({
        id: "todos",
        descricao: "Selecionar todos",
        tipo: "Selecionar todos",
        displayValue: "Selecionar todos",
      });
    }

    if (opcoes.length - 1 == value.length) {
      opcoes = opcoes.filter((item) => item.id != "todos");
      opcoes.unshift({
        id: "todos",
        descricao: "Retirar todos",
        tipo: "Retirar todos",
        displayValue: "Retirar todos",
      });
    }
  }

  const selecionarItem = (item, valor) => {
    try {
      const opcaoJaExiste = (obj) => obj.id === "todos";

      if (valor.some(opcaoJaExiste)) {
        if (opcoes.length - 1 == value.length) {
          onChange(item, []);
        } else {
          onChange(
            item,
            opcoes.filter((item) => item.id != "todos"),
          );
        }
      } else {
        onChange(
          item,
          valor.filter((item) => item.id != "todos"),
        );
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (buscarOpcoes) {
      buscarOpcoes(texto);
    }
  }, [texto]);

  return (
    <AutocompleteCustomizado
      disabled={disabled}
      noOptionsText={t("placeholders.noRecordsRegistered")}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={!opcaoUnica}
      disableClearable={disableClearable}
      sx={{ ...style, width: "100%" }}
      getOptionLabel={(option) =>
        finalizadora
          ? option.finalizadoraDTO[referenciaNome]
          : option[referenciaNome]
            ? option[referenciaNome]
            : ""
      }
      options={opcoes}
      onChange={opcaoUnica ? onChange : selecionarItem}
      value={value}
      disablePortal={disablePortal}
      slotProps={{
        paper: {
          sx: {
            overflow: "hidden",
          },
        },
      }}
      sxMaxHeight100={sxMaxHeight100}
      ListboxProps={{
        sx: {
          maxHeight: sxMaxHeight100 ? sxMaxHeight100 : 250,
          overflowY: "auto",
          overflowX: "hidden",

          "&::-webkit-scrollbar": {
            width: "8px",
            display: "block",
            backgroundColor: (theme) => theme.cores.brand20,
            borderRadius: "10px",
          },

          "&::-webkit-scrollbar-thumb": {
            borderRadius: "5px",
            backgroundColor: (theme) => theme.cores.surfaceActionActivePrimary,
          },

          "&::-webkit-scrollbar-track": {
            backgroundColor: (theme) => theme.adaptativo("brand20", "brand60"),
          },
        },
      }}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option[referenciaKey] + "autocomplete"}>
            {finalizadora ? option.finalizadoraDTO[referenciaNome] : option[referenciaNome]}
          </li>
        );
      }}
      renderInput={(params) => (
        <CampoDeTextoBaseAutoComplete
          finalizadoras={finalizadora}
          disabled={disabled}
          ref={refs}
          tabIndex={tabindex}
          onBlur={focusProximo}
          {...params}
          error={error}
          label={titulo}
          variant="standard"
          value={value}
          onChange={(e) => setTexto(e.target.value)}
          sx={
            iconeQuestion
              ? {
                  "& .MuiAutocomplete-endAdornment": {
                    right: 12,
                  },
                }
              : undefined
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}

                {iconeInfo && (
                  <Info
                    size={20}
                    id={iconeInfoId}
                    style={{
                      fill: "#757575",
                      color: "#757575",
                      cursor: "pointer",
                      marginLeft: 4,
                      marginRight: 2,
                    }}
                  />
                )}

                {iconeQuestion && (
                  <Question
                    id={iconeQuestionId}
                    color="rgba(0, 100, 149, 1)"
                    size={21}
                    style={{
                      position: "absolute",
                      right: 0,
                      bottom: 15,
                      cursor: "default",
                    }}
                  />
                )}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export function CampoAutoCompleteAssincrono({
  error = false,
  funcaoGetter = () => {},
  style,
  titulo,
}) {
  const [texto, setTexto] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const loading = open && options.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      if (active) {
        const items = await funcaoGetter(texto);
        if (items && items.length > 0) {
          setOptions([...items]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [loading, texto]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <AutocompleteCustomizado
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      sx={{ ...style, width: "100%", marginTop: "5px" }}
      isOptionEqualToValue={(option, value) => option.descricao === value.id}
      getOptionLabel={(option) => option.descricao}
      options={options}
      loading={loading}
      renderInput={(params) => (
        <CampoDeTextoBase
          {...params}
          error={error}
          label={titulo}
          variant="standard"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
    />
  );
}

export function CampoDeInputNumericoComSetas({
  style = {},
  onChange,
  value,
  label,
  error,
  onBlur = () => {},
  disabled,
  focusProximo,
  width = 220,
  maxValue = 9999999999999,
  esconderBotoes = false,
  onKeyDown = () => {},
  inputProps = {},
}) {
  // Aviso
  // Diferente dos outros campos de Input, por fazer o tratamento, este retorna diretamente o Valor, e não o
  // e.target.value como os outros.

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState(1);
  const { largura } = useTamanhoTela();

  useEffect(() => {
    if (value) {
      setRepresentacaoVisual(value);
    } else {
      setRepresentacaoVisual("");
    }
  }, [value]);

  function processar(dados) {
    if (onChange) {
      onChange(dados);
    } else if (onBlur) {
      onBlur(dados);
    }
  }

  function onChangeInterno(valorAtualizado) {
    const valor = valorAtualizado.target.value;

    processar(
      parseFloat(
        valor.replace("+", "").replaceAll("-", "").replaceAll(",", "").replaceAll(".", ""),
      ),
    );
    setRepresentacaoVisual(valor);
  }

  function onBlurInterno(valorAtualizado) {
    const valor = valorAtualizado.target.value;

    processar(
      parseFloat(
        valor.replace("+", "").replaceAll("-", "").replaceAll(",", "").replaceAll(".", ""),
      ),
    );
    setRepresentacaoVisual(valor);
  }

  function adicionarAoValor(e) {
    if (value >= maxValue) {
      return;
    }
    e.stopPropagation();
    processar(value >= 1 ? value + 1 : 1);
    setRepresentacaoVisual(representacaoVisual >= 1 ? representacaoVisual + 1 : 1);
  }

  function diminuirAoValor(e) {
    e.stopPropagation();
    processar(value >= 2 ? value - 1 : 1);
    setRepresentacaoVisual(representacaoVisual >= 2 ? representacaoVisual - 1 : 1);
  }

  return (
    <NumericFormatCustomizado
      disabled={disabled}
      style={{ pointerEvents: disabled ? "none" : "all", width: "100%" }}
    >
      <NumericFormatInput
        // {...rest}
        style={{
          ...style,
        }}
        InputLabelProps={{
          shrink: true,
        }}
        label={label}
        onChange={onChange ? onChangeInterno : () => {}}
        onBlur={focusProximo || onBlur ? onBlurInterno : () => {}}
        onKeyDown={onKeyDown}
        value={representacaoVisual}
        allowNegative={false}
        allowLeadingZeros={false}
        min={1}
        error={error}
        customInput={TextFieldMonetario}
        disabled={disabled}
        inputProps={{
          ...inputProps,
        }}
        {...propriedadesMaterialUIStandard}
      />
      <IconeBotao
        className="sub botao"
        onClick={(event) => (disabled ? {} : diminuirAoValor(event))}
        style={{
          fontSize: largura > 768 ? "18px" : "24px",
          display: esconderBotoes ? "none" : "block",
        }}
      >
        <FontAwesomeIcon icon={faMinus} />
      </IconeBotao>
      <IconeBotao
        className="add botao"
        onClick={(event) => (disabled ? {} : adicionarAoValor(event))}
        style={{
          fontSize: largura > 768 ? "18px" : "24px",
          display: esconderBotoes ? "none" : "block",
        }}
      >
        <FontAwesomeIcon icon={faPlus} />
      </IconeBotao>
    </NumericFormatCustomizado>
  );
}
export function CampoDeInputNumericoComSetasZero({
  disabled,
  error,
  focusProximo,
  label,
  maxValue = 9999999999999,
  onBlur = () => {},
  onChange,
  style = {},
  value,
}) {
  // Aviso
  // Diferente dos outros campos de Input, por fazer o tratamento, este retorna diretamente o Valor, e não o
  // e.target.value como os outros.

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState(0);
  const { largura } = useTamanhoTela();

  useEffect(() => {
    if (value) {
      setRepresentacaoVisual(value);
    } else {
      setRepresentacaoVisual("0");
    }
  }, [value]);

  function processar(dados) {
    if (onChange) {
      onChange(dados);
    } else if (onBlur) {
      onBlur(dados);
    }
  }

  function onChangeInterno(valorAtualizado) {
    const valor = valorAtualizado.target.value;

    processar(
      parseFloat(
        valor.replace("+", "").replaceAll("-", "").replaceAll(",", "").replaceAll(".", ""),
      ),
    );
    setRepresentacaoVisual(valor);
  }

  function onBlurInterno(valorAtualizado) {
    const valor = valorAtualizado.target.value;

    processar(
      parseFloat(
        valor.replace("+", "").replaceAll("-", "").replaceAll(",", "").replaceAll(".", ""),
      ),
    );
    setRepresentacaoVisual(valor);
  }

  function adicionarAoValor(e) {
    if (value >= maxValue) {
      return;
    }
    e.stopPropagation();
    processar(value >= 0 ? value + 1 : 0);
    setRepresentacaoVisual(representacaoVisual >= 0 ? representacaoVisual + 1 : 0);
  }

  function diminuirAoValor(e) {
    e.stopPropagation();
    processar(value > 0 ? value - 1 : 0);
    setRepresentacaoVisual(representacaoVisual > 0 ? representacaoVisual - 1 : 0);
  }

  return (
    <NumericFormatCustomizado
      disabled={disabled}
      style={{ pointerEvents: disabled ? "none" : "all", width: "100%" }}
    >
      <NumericFormatInput
        // {...rest}
        style={{
          ...style,
        }}
        InputLabelProps={{
          shrink: true,
        }}
        label={label}
        onChange={onChange ? onChangeInterno : () => {}}
        onBlur={focusProximo || onBlur ? onBlurInterno : () => {}}
        value={representacaoVisual}
        allowNegative={false}
        allowLeadingZeros={false}
        min={0}
        error={error}
        customInput={TextFieldMonetario}
        disabled={disabled}
        {...propriedadesMaterialUIStandard}
      />
      <IconeBotao
        className="sub botao"
        onClick={(event) => (disabled ? {} : diminuirAoValor(event))}
        style={{ fontSize: largura > 768 ? "18px" : "24px" }}
      >
        <FontAwesomeIcon icon={faMinus} />
      </IconeBotao>
      <IconeBotao
        className="add botao"
        onClick={(event) => (disabled ? {} : adicionarAoValor(event))}
        style={{ fontSize: largura > 768 ? "18px" : "24px" }}
      >
        <FontAwesomeIcon icon={faPlus} />
      </IconeBotao>
    </NumericFormatCustomizado>
  );
}

export function CampoDeInputNumericoComSetasVerticais({
  apenasInteiros = false,
  desabilitarDigitacao = false,
  disabled,
  error,
  focusProximo,
  label,
  maxValue = 9999999999999,
  onBlur = () => {},
  onChange,
  style = {},
  value,
  valorMinimo = 0,
}) {
  // Aviso
  // Diferente dos outros campos de Input, por fazer o tratamento, este retorna diretamente o Valor, e não o
  // e.target.value como os outros.

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState(0);

  useEffect(() => {
    if (value) {
      setRepresentacaoVisual(value);
    } else {
      setRepresentacaoVisual(String(valorMinimo) == "NaN" ? 0 : valorMinimo);
    }
  }, [value]);

  function processar(dados) {
    if (onChange) {
      onChange(dados);
    } else if (onBlur) {
      onBlur(dados);
    }
  }

  function onChangeInterno(valorAtualizado) {
    const valor = valorAtualizado.target.value;

    processar(parseFloat(valor.replace("+", "").replaceAll("-", "").replaceAll(",", "")));
    setRepresentacaoVisual(valor);
  }

  function onBlurInterno(valorAtualizado) {
    let valor = valorAtualizado.target.value;
    if (valor == "" || String(valor) == "NaN") {
      valor = String(valorMinimo);
    }

    processar(parseFloat(valor.replace("+", "").replaceAll("-", "").replaceAll(",", "")));
    setRepresentacaoVisual(valor > valorMinimo ? valor : valorMinimo);
  }

  function adicionarAoValor(e) {
    if (value >= maxValue) {
      return;
    }
    e.stopPropagation();
    processar(value >= 0 ? value + 1 : 0);
    setRepresentacaoVisual(representacaoVisual >= 0 ? representacaoVisual + 1 : 0);
  }

  function diminuirAoValor(e) {
    e.stopPropagation();
    processar(value > valorMinimo ? value - 1 : valorMinimo);
    setRepresentacaoVisual(
      representacaoVisual > valorMinimo ? representacaoVisual - 1 : valorMinimo,
    );
  }

  return (
    <NumericFormatCustomizadoVertical
      disabled={disabled}
      style={{ pointerEvents: disabled ? "none" : "all", width: "100%" }}
    >
      <NumericFormatInput
        style={{
          ...style,
        }}
        InputLabelProps={{
          shrink: true,
        }}
        label={label}
        onChange={onChange ? onChangeInterno : () => {}}
        onBlur={focusProximo || onBlur ? onBlurInterno : () => {}}
        value={representacaoVisual}
        allowNegative={false}
        allowLeadingZeros={false}
        // apenasInteiros: sem casas decimais e sem separador decimal (ex.: quantidade de volumes).
        {...(apenasInteiros ? { decimalScale: 0, allowedDecimalSeparators: [] } : {})}
        min={valorMinimo}
        error={error}
        customInput={TextFieldMonetario}
        disabled={disabled}
        inputProps={{ readOnly: desabilitarDigitacao }}
        {...propriedadesMaterialUIStandard}
      />
      <IconeBotao
        className="subvertical botao"
        onClick={(event) => (disabled ? {} : diminuirAoValor(event))}
        style={{ fontSize: "14px" }}
      >
        <FontAwesomeIcon icon={faAngleDown} />
      </IconeBotao>
      <IconeBotao
        className="addvertical botao"
        onClick={(event) => (disabled ? {} : adicionarAoValor(event))}
        style={{ fontSize: "14px" }}
      >
        <FontAwesomeIcon icon={faAngleUp} />
      </IconeBotao>
    </NumericFormatCustomizadoVertical>
  );
}

export const CampoDeInputNumericoAlternativo = ({
  titulo,
  fundoTransparente = false,
  style,
  value,
  tabindex,
  refs,
  typeNumber = false,
  ocultarSetas = false,
  focusProximo,
  onBlur,
  disabled = false,
  variant = "standard",
  onKeyDown = () => {},
  className = "",
  type = "text",
  maxlength = 999,
  register,
  ...rest
}) => {
  // Filtrar propriedades que não devem ser passadas para o DOM
  const { typeNumber: _, ocultarSetas: __, $fundoTransparente: ___, ...filteredRest } = rest;

  return (
    <CampoDeInputNumericoAlternativoBase
      {...filteredRest}
      className={className}
      typeNumber={typeNumber}
      label={titulo}
      $fundoTransparente={fundoTransparente}
      variant={variant}
      style={{
        ...style,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
      value={value}
      onBlur={focusProximo || onBlur}
      inputProps={{ tabIndex: tabindex, maxLength: maxlength }}
      ref={refs}
      ocultarSetas={ocultarSetas}
      onKeyDown={onKeyDown}
      type={type}
      disabled={disabled}
      {...register}
    />
  );
};

export function CampoDeInputPorcentagem({
  style = {},
  onChange,
  value,
  label,
  error,
  onBlur = () => {},
  disabled,
  refs,
  refInputProps,
  noLimit100 = false,
  permitirZero = false,
  limit99 = false,
  sufix = true,
  tabIndex,
}) {
  const [valorInicialSetado, setValorInicialSetado] = useState(false);
  const [representacaoVisual, setRepresentacaoVisual] = useState("");

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
    InputProps: {
      placeholder: `${sufix ? "0.00%" : ""}`,
    },
    InputLabelProps: {
      shrink: permitirZero ? true : undefined,
    },
  };

  function onChangeInterno(valorAtualizado) {
    let valor = parseFloat(valorAtualizado.target.value.replace(/%|,/g, ""));

    if (isNaN(valor) && permitirZero && valorAtualizado.target.value === "0") {
      valor = 0;
    }

    onChange(valor);
    setRepresentacaoVisual(valorAtualizado.target.value);
  }

  const formatCurrencyByEnd = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const numericValue = Number(value);
    if (isNaN(numericValue)) return "";
    if (numericValue === 0 && !permitirZero) return "";
    const numeric = numericValue / 100;
    if (limit99 && numeric > 99.99) return `99.99${sufix ? "%" : ""}`;
    if (!limit99 && !noLimit100 && numeric > 100) return `100.00${sufix ? "%" : ""}`;

    const amount = new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);

    return `${amount.replace(",", ".")}${sufix ? "%" : ""}`;
  };

  useEffect(() => {
    if (value === "" || (value === 0 && !permitirZero)) {
      setRepresentacaoVisual("");
    } else {
      setRepresentacaoVisual(value?.toLocaleString("pt-br", { minimumFractionDigits: 2 }));
    }
  }, [value, permitirZero]);

  useEffect(() => {
    if (!valorInicialSetado) {
      if (value !== undefined && value !== null) {
        setValorInicialSetado(true);
        setRepresentacaoVisual(value.toLocaleString("pt-br", { minimumFractionDigits: 2 }));
      }
    }
  });

  return (
    <NumberFormatBaseInput
      className="input-CampoDeInputPorcentagem"
      customInput={TextFieldMonetario}
      decimalScale={2}
      decimalSeparator=","
      disabled={disabled}
      error={error}
      fixedDecimalScale
      format={formatCurrencyByEnd}
      getInputRef={refInputProps}
      inputProps={tabIndex != null ? { tabIndex } : undefined}
      label={label}
      maxLength={5}
      onBlur={onBlur}
      onChange={onChangeInterno}
      ref={refs}
      style={{ ...style }}
      suffix=" %"
      thousandSeparator="."
      value={representacaoVisual}
      {...propriedadesMaterialUIStandard}
      InputLabelProps={{ shrink: !!representacaoVisual ? true : undefined }}
    />
  );
}

export const CampoDeHora = ({ titulo, style, error, onBlur, ...rest }) => {
  return (
    <ContainerData style={{ ...style }}>
      <CampoDeHoraBase
        {...rest}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        }}
        label={titulo}
        variant="standart"
        slotProps={{
          textField: { variant: "standard", error: error, onBlur: onBlur },
        }}
      />
    </ContainerData>
  );
};

export const CampoDeHoraV2 = ({ titulo, style, error, limparCampo, onBlur, ...rest }) => {
  return (
    <ContainerData style={{ ...style }}>
      <CampoDeHoraBase
        {...rest}
        ampm={false}
        format="HH:mm"
        views={["hours", "minutes"]}
        label={titulo}
        variant="standard"
        localeText={{
          fieldHoursPlaceholder: () => "HH",
          fieldMinutesPlaceholder: () => "MM",
        }}
        slotProps={{
          field: {
            clearable: true,
            onClear: limparCampo,
          },
          textField: {
            variant: "standard",
            error: error,
            onBlur: onBlur,
            placeholder: "HH:MM",
          },
        }}
      />
    </ContainerData>
  );
};

export const CampoDeDataSemHora = ({
  error,
  required,
  errorMessage,
  limparCampo,
  refs,
  style,
  titulo,
  ...rest
}) => {
  return (
    <CampoDeDataSemHoraBase
      {...rest}
      ref={refs}
      label={titulo}
      variant="standart"
      dayOfWeekFormatter={(day) => day.charAt(0).toUpperCase()}
      slotProps={{
        textField: {
          variant: "standard",
          error: error,
          required: required,
          helperText: errorMessage,
        },
        ...(limparCampo
          ? {
              field: {
                clearable: true,
                onClear: limparCampo,
              },
            }
          : {}),
      }}
    />
  );
};

export const DescricaoErro = ({ erro, fontSize, marginBottom, style, ...rest }) => {
  const theme = useTheme();

  if (!erro?.message) {
    return null;
  }

  return (
    <div
      style={{
        marginBottom: marginBottom ? marginBottom : 0,
        position: "relative",
        height: 1,
        minHeight: 1,
        width: "100%",
        overflow: "visible",
      }}
    >
      <p
        {...rest}
        style={{
          ...style,
          color: theme.cores.semanticDanger2,
          fontSize: fontSize ? fontSize : 14,
          fontWeight: "bold",
          left: 0,
          lineHeight: 1.25,
          marginBottom: 4,
          marginLeft: 0,
          marginRight: "auto",
          marginTop: 0,
          overflowWrap: "anywhere",
          position: "absolute",
          textAlign: "left",
          top: 2,
          whiteSpace: "normal",
          width: "100%",
          wordBreak: "break-word",
        }}
      >
        {erro?.message}
      </p>
    </div>
  );
};

export const CampoDeTextoMeioPublicidade = ({
  titulo,
  fundoTransparente = false,
  style,
  value,
  ...rest
}) => {
  return (
    <CampoDeTextoCustom {...rest} label={titulo} variant="standard" style={style} value={value} />
  );
};

export function CampoDeInputPorcentagemWayservice({
  style = {},
  onChange,
  value,
  label,
  error,
  onBlur = () => {},
  disabled,
  refs,
  className,
}) {
  // Aviso
  // Diferente dos outros campos de Input, por fazer o tratamento, este retorna diretamente o Valor, e não o
  // e.target.value como os outros.

  const [valorInicialSetado, setValorInicialSetado] = useState();

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",

    InputProps: {
      InputAdornment: <InputAdornment position="start">%</InputAdornment>,
      placeholder: "%",
    },
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState("");

  function onChangeInterno(valorAtualizado) {
    const valor = parseFloat(valorAtualizado.target.value.replace("%", ""));

    onChange(valor);

    setRepresentacaoVisual(valorAtualizado.target.value);
  }

  const formatCurrencyByEnd = (value) => {
    if (!Number(value)) return "";

    if (parseFloat(value) / 100 > 100) return "100.00%";

    const amount = new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
    }).format(parseFloat(value) / 100);

    return `${amount.replace(",", ".")}%`;
  };

  useEffect(() => {
    if (value == "") {
      setRepresentacaoVisual("");
    }
  }, [value]);

  useEffect(() => {
    if (!valorInicialSetado) {
      if (value != undefined) {
        setValorInicialSetado(true);
        setRepresentacaoVisual(value.toLocaleString("pt-br", { minimumFractionDigits: 2 }));
      }
    }
  });

  return (
    <InputPorcentagemWayservice
      style={{ ...style }}
      className={className}
      format={formatCurrencyByEnd}
      label={label}
      //onValueChange={onChangeInterno}
      onChange={onChangeInterno}
      onBlur={onBlur}
      value={representacaoVisual}
      thousandSeparator="."
      decimalSeparator=","
      sufix=" %"
      maxLength={5}
      max={5}
      decimalScale={2}
      fixedDecimalScale
      error={error}
      customInput={TextFieldMonetario}
      disabled={disabled}
      {...propriedadesMaterialUIStandard}
    />
  );
}

export const CampoDeSelecaoTelaCardapio = ({
  titulo,
  referenciaNome,
  referenciaValor,
  valor,
  onChange,
  items = [],
  campoVazio = true,
  iconeSearch = false,
  cor = null,
  refs,
  classes = null,
  cardapioAvancado = false,
  ...rest
}) => {
  const theme = useTheme();

  function GerenciarObjetosOuTextos() {
    return items
      .map((item, index) => {
        // Verificação de segurança para itens undefined ou null
        if (!item) {
          return null;
        }

        if (typeof item === "string") {
          return (
            <MenuItem
              key={titulo + item + index}
              value={item}
              className={cardapioAvancado && "cardapioAvancado"}
            >
              {item}
            </MenuItem>
          );
        } else if (typeof item === "object") {
          // Verificação adicional para garantir que as propriedades existem
          const valorItem = item[referenciaValor ? referenciaValor : "valor"];
          const nomeItem = item[referenciaNome ? referenciaNome : "titulo"];

          // Se não tiver valor ou nome, pular este item
          if (valorItem === undefined || valorItem === null) {
            return null;
          }

          return (
            <MenuItem
              style={{ marginLeft: 50 }}
              key={titulo + valorItem + index}
              value={valorItem}
              className={cardapioAvancado && "cardapioAvancado"}
            >
              {nomeItem || valorItem}
            </MenuItem>
          );
        }

        return null;
      })
      .filter(Boolean); // Remove itens null do array
  }

  return (
    <FormularioCustomizadoSelecao variant="standard" {...rest} value={valor} cor={cor}>
      <InputLabel
        style={{
          display: iconeSearch ? "flex" : "",
          alignItems: iconeSearch ? "center" : "",
          gap: iconeSearch ? 10 : "",
          paddingLeft: iconeSearch ? 16 : "",
          height: "35px",
        }}
      >
        {iconeSearch && (
          <FontAwesomeIcon
            icon={faSearch}
            style={{
              color: theme.adaptativo("neutral50", "neutral100"),
              width: 15,
              height: 15,
              weight: "bold",
            }}
          />
        )}
        {titulo}
      </InputLabel>
      <Select
        id="zeca"
        className={classes}
        ref={refs}
        value={items.length > 0 && valor ? valor : ""}
        label={titulo}
        onChange={onChange}
        style={{ height: "35px", color: `rgb(${cor})` }}
        MenuProps={{ variant: "menu" }}
      >
        {(campoVazio || !valor) && <MenuItem value="">Selecione</MenuItem>};
        {GerenciarObjetosOuTextos()}
      </Select>
    </FormularioCustomizadoSelecao>
  );
};

export const CampoCor = ({ value, onChange, style, label, disabled = false, ...rest }) => {
  return (
    <CampoCorBase
      label={label}
      variant="standard"
      style={{
        ...style,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
      value={value}
      onChange={onChange}
      disabled={disabled}
      {...rest}
    />
  );
};

export function CampoDeInputPixel({
  style = {},
  onChange,
  value,
  label,
  error,
  onBlur = () => {},
  disabled,
  refs,
}) {
  // Aviso
  // Diferente dos outros campos de Input, por fazer o tratamento, este retorna diretamente o Valor, e não o
  // e.target.value como os outros.

  const [valorInicialSetado, setValorInicialSetado] = useState(false);

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",

    InputProps: {
      InputAdornment: <InputAdornment position="start">px</InputAdornment>,
      placeholder: "px",
    },
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState("");

  function onChangeInterno(valorAtualizado) {
    const valor = parseFloat(valorAtualizado.target.value);

    onChange(valor);

    setRepresentacaoVisual(valorAtualizado.target.value);
  }

  const formatCurrencyByEnd = (value) => {
    if (!Number(value)) return "";

    if (parseFloat(value).toFixed(2) > 999000.0) {
      toast.warning(`O valor máximo é de 1000.00px`);
      return "1000.00";
    }

    const amount = new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
    }).format(parseFloat(value) / 100);

    return `${amount.replace(",", ".")}`;
  };

  useEffect(() => {
    if (value == "") {
      setRepresentacaoVisual("");
    }
  }, [value]);

  useEffect(() => {
    if (!valorInicialSetado) {
      if (value != undefined) {
        setValorInicialSetado(true);
        setRepresentacaoVisual(value.toLocaleString("pt-br", { minimumFractionDigits: 2 }));
      }
    }
  });

  return (
    <NumberFormatBaseInput
      className="input-CampoDeInputPorcentagem"
      format={formatCurrencyByEnd}
      label={label}
      onChange={onChangeInterno}
      onBlur={onBlur}
      value={representacaoVisual}
      thousandSeparator="."
      decimalSeparator=","
      sufix=" %"
      maxLength={5}
      max={5}
      decimalScale={2}
      fixedDecimalScale
      error={error}
      customInput={TextFieldMonetario}
      disabled={disabled}
      ref={refs}
      {...propriedadesMaterialUIStandard}
      style={{
        ...style,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
      InputLabelProps={{ shrink: !!representacaoVisual ? true : undefined }}
    />
  );
}

export function CampoDeInputNumeroDecimal({
  style = {},
  onChange = () => {},
  onFocus = () => {},
  value,
  label,
  titulo,
  error,
  onBlur = () => {},
  pedidoCompra = false,
  valorMax = 99999999.99,
  tabindex,
  onKeyDown = () => {},
  refs,
  classess = "",
  disabled = false,
  semValorMaximo = false,
  limiteDigitos,
  // Mostra "0,000" quando o valor é 0 (padrão de campos como Peso bruto/líquido); sem a prop, 0 fica vazio.
  exibirZero = false,
}) {
  const [valorInicialSetado, setValorInicialSetado] = useState(false);
  // Aviso
  // Diferente dos outros campos de Input, por fazer o tratamento, este retorna diretamente o Valor, e não o
  // e.target.value como os outros.

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
    placeholder: "0,00",
    InputProps: {
      placeholder: "0,00",
    },
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState(
    value != "" && value != 0 && value != undefined
      ? String(value.toFixed(3)).replace(".", ",")
      : exibirZero && value === 0
        ? "0,000"
        : "",
  );

  useEffect(() => {
    if (value == "") {
      setRepresentacaoVisual("");
    } else {
      setRepresentacaoVisual(
        value != "" && value != 0 && value != undefined
          ? String(value.toFixed(3)).replace(".", ",")
          : exibirZero && value === 0
            ? "0,000"
            : "",
      );
    }
  }, [value]);

  function onChangeInterno(valorAtualizado) {
    const valorLimpo = valorAtualizado.target.value.replace(",", "");
    if (valorLimpo.length > limiteDigitos) {
      return;
    }
    let valor = parseFloat(
      valorAtualizado.target.value.replace("R$", "").replaceAll(".", "").replaceAll(",", "."),
    );

    if (semValorMaximo) {
      valor = tratarLimiteValor(valor);
    }

    onChange(valor);
    setRepresentacaoVisual(valorAtualizado.target.value);
  }

  function tratarLimiteValor(valor) {
    let novoValor = valor;
    novoValor = semValorMaximo ? novoValor : tratarValorMax(novoValor);
    return novoValor;
  }

  function tratarValorMax(valor) {
    if (valorMax || pedidoCompra) {
      const valorMaxAtualizado = pedidoCompra ? 99999.99 : valorMax;
      const max = valorMaxAtualizado * 100;
      if (valor > max) {
        return max;
      }
    }

    return valor;
  }

  const formatCurrencyByEnd = (value) => {
    if (!Number(value)) return exibirZero ? "0,000" : "";

    let valor;
    if (value.length > limiteDigitos) {
      valor = tratarLimiteValor(value.substring(0, limiteDigitos));
    } else {
      valor = tratarLimiteValor(value);
    }

    const amount = new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 3,
    }).format(parseFloat(valor) / 1000);

    return `${amount}`;
  };

  return (
    <NumberFormatBaseInput
      className={`input-CampoDeInputNumeroDecimal ${classess}`}
      // {...rest}
      format={formatCurrencyByEnd}
      style={{
        ...style,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
      label={label || titulo}
      //onValueChange={onChangeInterno}
      onChange={onChangeInterno}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onFocus={onFocus}
      value={representacaoVisual}
      error={error}
      customInput={TextFieldMonetario}
      disabled={disabled}
      {...propriedadesMaterialUIStandard}
      ref={refs}
      InputProps={{ tabIndex: tabindex }}
      InputLabelProps={{ shrink: !!representacaoVisual ? true : undefined }}
    />
  );
}

export function CampoAutoCompleteLazyLoading({
  titulo,
  value,
  onChange,
  funcaoBuscar,
  referenciaId = "id",
  referenciaNome = "descricao",
  error = false,
  disabled = false,
  style,
  mostrarRemover = true,
  desabilitarScroll = false,
  styleListBox,
}) {
  const { t } = useTranslation("common");
  const [opcoes, setOpcoes] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [ultimaPagina, setUltimaPagina] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const buscaAtualRef = useRef("");
  const debounceRef = useRef(null);

  const carregarOpcoes = useCallback(
    async (page, filtro, substituir) => {
      setCarregando(true);
      try {
        const resultado = await funcaoBuscar({ page, filtroGeral: filtro });
        const novasOpcoes = resultado?.content ?? [];
        setOpcoes((prev) => (substituir ? novasOpcoes : [...prev, ...novasOpcoes]));
        setUltimaPagina(resultado?.last ?? true);
        setPaginaAtual(page);
      } catch (e) {
        console.error(e);
      } finally {
        setCarregando(false);
      }
    },
    [funcaoBuscar],
  );

  useEffect(() => {
    if (aberto) {
      buscaAtualRef.current = "";
      carregarOpcoes(0, "", true);
    } else {
      setOpcoes([]);
      setPaginaAtual(0);
      setUltimaPagina(false);
    }
  }, [aberto]);

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason !== "input") return;
    buscaAtualRef.current = newInputValue;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      carregarOpcoes(0, newInputValue, true);
    }, 400);
  };

  const handleScroll = (event) => {
    if (desabilitarScroll) return;
    const listbox = event.currentTarget;
    if (
      listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 50 &&
      !ultimaPagina &&
      !carregando
    ) {
      carregarOpcoes(paginaAtual + 1, buscaAtualRef.current, false);
    }
  };

  return (
    <AutocompleteCustomizado
      disabled={disabled}
      open={aberto}
      onOpen={() => setAberto(true)}
      onClose={() => setAberto(false)}
      options={opcoes}
      value={value ?? null}
      onInputChange={handleInputChange}
      disableClearable={!mostrarRemover}
      getOptionLabel={(option) =>
        typeof option === "object" ? (option?.[referenciaNome] ?? "") : ""
      }
      isOptionEqualToValue={(option, val) => option?.[referenciaId] === val?.[referenciaId]}
      onChange={(event, newValue) => onChange(newValue)}
      loading={carregando}
      loadingText={t("placeholders.loading")}
      noOptionsText={t("placeholders.noRecordsFound")}
      filterOptions={(x) => x}
      sx={{ ...style, width: "100%" }}
      ListboxProps={{
        onScroll: handleScroll,
        style: {
          maxHeight: 140,
          height: 140,
          overflowY: "scroll",
          ...styleListBox,
        },
      }}
      renderOption={(props, option) => (
        <li {...props} key={option[referenciaId] + "lazy"}>
          {option[referenciaNome]}
        </li>
      )}
      renderInput={(params) => (
        <CampoDeTextoBaseAutoComplete
          {...params}
          label={titulo}
          variant="standard"
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {carregando ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
    />
  );
}

export function CampoDeInputNumericoDecimalDuasCasas({
  classess = "",
  tipo,
  titulo,
  value,
  onChange,
  onBlur = () => {},
  onFocus,
  valorMax = 99999999.99,
  style,
  label,
  error,
  disabled = false,
  tabindex,
  onKeyDown = () => {},
  permitirZero,
  refs,
  ...rest
}) {
  const limiteDigitos = valorMax ? Math.floor(Math.log10(valorMax)) + 1 + 2 : 15;

  const propriedadesMaterialUIStandard = {
    id: "standard-basic",
    variant: "standard",
    placeholder: "0,00",
    InputProps: {
      placeholder: "0,00",
    },
  };

  const [representacaoVisual, setRepresentacaoVisual] = useState("");

  useEffect(() => {
    if (value !== undefined && value !== null && value !== "") {
      const valorEmCentavos = Math.round(parseFloat(value) * 100);
      setRepresentacaoVisual(valorEmCentavos.toString());
    } else if (value === 0 && permitirZero) {
      setRepresentacaoVisual("0");
    } else {
      setRepresentacaoVisual("");
    }
  }, [value, permitirZero]);

  const onChangeInterno = useCallback(
    (event) => {
      const valorDigitado = event.target.value;

      const valorNumerico = valorDigitado.replace(/[^\d]/g, "");

      if (!valorNumerico) {
        setRepresentacaoVisual("");
        if (onChange) {
          onChange(permitirZero ? 0 : "");
        }
        return;
      }

      setRepresentacaoVisual(valorNumerico);

      if (onChange) {
        const valorReal = parseFloat(valorNumerico) / 100;
        onChange(valorReal);
      }
    },
    [onChange, permitirZero],
  );

  function tratarLimiteValor(valor) {
    if (valorMax) {
      const valorMaxFormatado = parseFloat(valorMax.toFixed(2));
      const valorMaxSemDecimal = parseFloat(valorMaxFormatado.toString().replace(".", ""));

      const valorDigitadoSemDecimal = parseFloat(valor);

      if (valorDigitadoSemDecimal > valorMaxSemDecimal) {
        valor = valorMaxSemDecimal.toString();
      }
    }

    return valor;
  }

  const formatCurrencyByEnd = (value) => {
    if (value === "" || value === null || value === undefined || value == "0") return "";

    let valor;
    if (value.length > limiteDigitos) {
      valor = tratarLimiteValor(value.substring(0, limiteDigitos));
    } else {
      valor = tratarLimiteValor(value);
    }

    const amount = new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
    }).format(parseFloat(valor) / 100);

    return `R$ ${amount}`;
  };

  return (
    <NumberFormatBaseInput
      className={`input-CampoDeInputNumericoDecimalDuasCasas ${classess}`}
      format={formatCurrencyByEnd}
      style={{
        ...style,
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
      label={label || titulo}
      onChange={onChangeInterno}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onFocus={onFocus}
      value={representacaoVisual}
      error={error}
      customInput={TextFieldMonetario}
      disabled={disabled}
      {...propriedadesMaterialUIStandard}
      ref={refs}
      InputProps={{ tabIndex: tabindex, placeholder: "0,00" }}
      InputLabelProps={{ shrink: !!representacaoVisual ? true : undefined }}
    />
  );
}
